import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

const MapleLeaf = ({ className, style }) => (
  <svg viewBox="0 0 100 100" className={className} style={style} fill="currentColor">
    <path d="M50 97 L50 60 M50 60 C45 55 30 58 25 50 C30 50 35 45 35 40 C25 42 15 38 10 30 C20 32 25 28 30 22 C25 20 22 12 25 5 C32 12 40 15 50 10 C60 15 68 12 75 5 C78 12 75 20 70 22 C75 28 80 32 90 30 C85 38 75 42 65 40 C65 45 70 50 75 50 C70 58 55 55 50 60"/>
  </svg>
);

const fallingLeaves = [
  { id: 1, size: 50, left: '15%', delay: 0, duration: 14, color: '#1a5c1e' },
  { id: 2, size: 40, left: '75%', delay: 2, duration: 17, color: '#a6533a' },
  { id: 3, size: 46, left: '45%', delay: 4, duration: 13, color: '#2d5a2d' },
  { id: 4, size: 36, left: '85%', delay: 1, duration: 18, color: '#8b6b4a' },
  { id: 5, size: 52, left: '30%', delay: 3, duration: 15, color: '#1a5c1e' },
  { id: 6, size: 42, left: '60%', delay: 6, duration: 16, color: '#6b5344' },
];

const trainingsData = [
  {
    id: 'esg',
    num: '01',
    name: 'ESG Fundamentals & Disclosure',
    tagline: 'Cut through the framework noise.',
    text: 'Practical understanding of what ESG means for your organization — beyond checklists and compliance.',
    image: 'https://images.unsplash.com/photo-1760124056883-732e7a5e2e68?w=1200&q=80',
    items: [
      'ESG frameworks in practice — CSRD, ISSB, GRI, TCFD, SASB',
      'Materiality assessment and stakeholder engagement',
      'Data collection, governance, and disclosure readiness',
      'Board and executive ESG governance briefings'
    ]
  },
  {
    id: 'finance',
    num: '02',
    name: 'Climate Finance & Blended Structures',
    tagline: 'Fluency at the intersection of finance and sustainability.',
    text: 'From DFI instruments to blended structures — for teams that need to navigate climate capital confidently.',
    image: 'https://images.unsplash.com/photo-1698752821935-70289795bf9e?w=1200&q=80',
    items: [
      'Blended finance mechanics and concessional instruments',
      'DFI and MDB landscape navigation',
      'Impact measurement and reporting frameworks',
      'Green and sustainability-linked debt'
    ]
  },
  {
    id: 'carbon',
    num: '03',
    name: 'Carbon Markets & Project Development',
    tagline: 'For practitioners, not policy audiences.',
    text: 'Voluntary and compliance markets, credit quality, and the full project development lifecycle.',
    image: 'https://images.unsplash.com/photo-1593198095345-311f4d23d511?w=1200&q=80',
    items: [
      'VCM structure — registries, methodologies, buyer landscape',
      'Article 6 and compliance market navigation',
      'Project development — additionality, baselines, PDD design',
      'CBAM fundamentals and embedded carbon calculation'
    ]
  },
  {
    id: 'dmrv',
    num: '04',
    name: 'dMRV & Carbon Data Infrastructure',
    tagline: 'Where carbon project integrity is won or lost.',
    text: 'Practical capability in digital monitoring, data management, and verification readiness.',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&q=80',
    items: [
      'dMRV system design and methodology alignment',
      'IoT, remote sensing, and satellite data fundamentals',
      'Audit trails, version control, and data governance',
      'Verification readiness and platform walkthroughs'
    ]
  }
];

const TrainingPage = () => {
  const [activeSection, setActiveSection] = useState('esg');

  useEffect(() => {
    const handleScroll = () => {
      for (const t of trainingsData) {
        const el = document.getElementById(t.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom > 200) {
            setActiveSection(t.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <main data-testid="training-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1601655856683-8400aa9ef59f?w=1920&q=80"
            alt="Abstract canvas"
            className="w-full h-full object-cover"
            style={{ filter: 'saturate(0.5) brightness(1.15) contrast(0.9) sepia(0.1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-white" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/70 via-transparent to-white/70" />
        </div>

        {/* Falling Maple Leaves */}
        <div className="absolute inset-0 z-[2] overflow-hidden pointer-events-none">
          {fallingLeaves.map((leaf) => (
            <motion.div
              key={leaf.id}
              className="absolute"
              style={{ left: leaf.left, top: -50 }}
              initial={{ y: -50, rotate: 0, opacity: 0 }}
              animate={{ 
                y: ['0vh', '110vh'],
                rotate: [0, 360, 180, 360],
                opacity: [0, 0.45, 0.45, 0]
              }}
              transition={{ duration: leaf.duration, delay: leaf.delay, repeat: Infinity, ease: "linear" }}
            >
              <motion.div
                animate={{ x: [0, -30, 15, -20, 0], rotateY: [0, 180, 360] }}
                transition={{ duration: leaf.duration / 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <MapleLeaf className="drop-shadow-sm" style={{ width: leaf.size, height: leaf.size, color: leaf.color }} />
              </motion.div>
            </motion.div>
          ))}
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-brand-green/20 mb-8"
            >
              <Sparkles className="w-4 h-4 text-brand-green" />
              <span className="font-sans text-sm font-semibold text-brand-green uppercase tracking-[0.2em]">Training</span>
            </motion.div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-6xl text-brand-dark tracking-tight leading-none mb-6">
              Training &{' '}
              <span className="bg-gradient-to-r from-brand-brown to-brand-green bg-clip-text text-transparent">
                Capacity Building
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-serif text-lg md:text-xl text-gray-600 italic mb-10"
            >
              Sustainability knowledge that sticks.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Link to="/contact">
                <Button data-testid="hero-cta" className="bg-brand-brown hover:bg-brand-brown/90 text-white font-sans font-medium px-8 py-5 rounded-sm text-sm shadow-lg">
                  Book a Session <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Content: Sticky Sidebar + Scrolling Feed */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12">
            
            {/* Sticky Navigation - Left */}
            <div className="hidden md:block md:col-span-4 lg:col-span-3">
              <div className="sticky top-32">
                <span className="font-mono text-xs text-brand-brown uppercase tracking-[0.2em] mb-6 block">Syllabus</span>
                <nav className="space-y-1" data-testid="training-sidebar-nav">
                  {trainingsData.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`sidebar-nav-${t.id}`}
                      onClick={() => document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className={`w-full text-left py-3 px-4 border-l-2 transition-all duration-300 hover:translate-x-1 ${
                        activeSection === t.id
                          ? 'border-brand-green text-brand-green font-medium bg-brand-green/5'
                          : 'border-transparent text-brand-dark/40 hover:text-brand-dark hover:border-gray-300'
                      }`}
                    >
                      <span className="font-mono text-xs mr-3 opacity-60">{t.num}</span>
                      <span className="font-sans text-sm">{t.name}</span>
                    </button>
                  ))}
                </nav>

                <div className="mt-10 pt-8 border-t border-gray-100">
                  <p className="font-sans text-xs text-gray-400 leading-relaxed mb-4">
                    All programs are tailored to your team's level, sector, and strategic priorities.
                  </p>
                  <Link to="/contact">
                    <Button data-testid="sidebar-cta" variant="outline" className="w-full border-brand-green text-brand-green hover:bg-brand-green/5 rounded-sm text-xs font-sans">
                      Enquire Now
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Program Feed - Right */}
            <div className="col-span-12 md:col-span-8 lg:col-span-9 space-y-28">
              {trainingsData.map((training) => (
                <motion.article
                  key={training.id}
                  id={training.id}
                  data-testid={`program-${training.id}`}
                  initial={{ opacity: 0, y: 60 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7 }}
                  className="scroll-mt-32"
                >
                  {/* Full-width Image */}
                  <div className="relative overflow-hidden rounded-lg mb-8 group">
                    <img
                      src={training.image}
                      alt={training.name}
                      className="w-full aspect-[16/9] object-cover group-hover:scale-[1.03] transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/40 via-transparent to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <span className="inline-block font-mono text-xs text-white/80 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-sm border border-white/20">
                        Program {training.num}
                      </span>
                    </div>
                  </div>

                  {/* Number */}
                  <span className="font-mono text-6xl lg:text-8xl font-bold text-brand-green/10 leading-none block -mb-4 lg:-mb-6">
                    {training.num}
                  </span>

                  {/* Tagline */}
                  <p className="font-mono text-xs text-brand-brown uppercase tracking-[0.2em] mb-3">
                    {training.tagline}
                  </p>

                  {/* Title */}
                  <h2 className="font-serif text-3xl lg:text-4xl text-brand-dark tracking-tight leading-tight mb-4">
                    {training.name}
                  </h2>

                  {/* Description */}
                  <p className="font-sans text-base text-gray-600 leading-relaxed max-w-2xl mb-6">
                    {training.text}
                  </p>

                  {/* Curriculum Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 pt-6 border-t border-brand-green/20">
                    {training.items.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 group/item">
                        <CheckCircle2 className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0 opacity-60 group-hover/item:opacity-100 transition-opacity" />
                        <span className="font-sans text-sm text-gray-600 group-hover/item:text-brand-dark transition-colors">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Full width dark green */}
      <section data-testid="training-cta" className="py-20 bg-brand-dark relative overflow-hidden">
        <motion.div 
          animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.1, 0.05] }}
          transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-green/20 rounded-full blur-[150px]"
        />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight mb-6">
              Ready to build internal{' '}
              <span className="italic text-brand-green">sustainability capability</span>?
            </h2>
            <p className="font-sans text-base text-white/60 mb-10 max-w-xl mx-auto">
              Our training programs are tailored to your team's level, your sector, and your strategic priorities.
            </p>
            <Link to="/contact">
              <Button data-testid="cta-button" className="bg-brand-brown hover:bg-brand-brown/90 text-white font-sans font-medium px-10 py-6 rounded-sm text-base shadow-xl">
                Get in Touch <ArrowRight className="w-5 h-5 ml-3" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default TrainingPage;
